/**
 * FilterManager · narrows the wire to exactly what the SDK has interest in.
 *
 * Background:
 *   The JWT's `allow_sub` is a small generated set of roots (e.g.
 *   `morfi.market.*.>`, `morfi.leaderboard.>`). Server-side ACL is bound
 *   to those roots — the SDK can't subscribe outside them. Naïvely
 *   opening one JetStream consumer per root means every market's events
 *   flow to every client regardless of what's on-screen, which doesn't
 *   scale past a few hundred markets.
 *
 *   This module bridges the gap: it owns one consumer shard per root,
 *   adding another shard only when bounded filters overlap. Each shard's
 *   `filter_subjects` list is mutated as the SDK's
 *   in-memory observer trie gains/loses concrete subjects via the
 *   registry's `wireInterestRequest` / `wireInterestRelease` callbacks.
 *   Result: 10 visible markets → ≤ 30 concrete subjects on the wire,
 *   not "every event matching the wildcard."
 *
 * Data flow:
 *
 *   row enters viewport → registry.registerObserver(subject)
 *                       → 0→1 transition fires wireInterestRequest(subject)
 *                       → FilterManager.add(subject)
 *                       → desired.add; pending diff; debounce 100 ms
 *                       → flush() → partition non-overlapping filter shards
 *                       → per-shard consumer.update({ filter_subjects })
 *
 *   row leaves viewport → registry.deregisterObserver(...)
 *                       → 1→0 transition fires wireInterestRelease(subject)
 *                       → FilterManager.remove(subject)
 *                       → 2 s grace timer, then remove from desired + flush
 *
 * Empty-viewport handling: when a pattern's `desired` is empty we still
 * call `update({ filter_subjects: [SENTINEL] })` where SENTINEL is a
 * subject covered by the pattern but never published by the indexer.
 * Keeps the consumer's `lastSeq` pointer alive without delivering
 * anything, so repopulating viewport is one round-trip away.
 *
 * Replay-on-reconnect: unchanged from the prior version. `lastSeq` is
 * still keyed per pattern root (`last-seq-store.ts`) — every event for
 * any concrete subject under that root advances the same anchor.
 */

import { reportWireHealth } from '../../connectivity/signals/connect-health.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe, _observeWireArrival } from '../../devtool/perf-histograms.js';
import type { MessageHandler } from '../../provider/useNatsSubscription.types.js';
import {
  classifyFailureReason,
  describeError,
  isConsumerStoreError,
  reportNatsFailure,
  reportNatsRecovery,
} from '../connect/report-nats-failure.js';
import { clearLastSeq, getLastSeq, recordLastSeq } from '../last-seq-store.js';
import { subjectMatches } from '../registry/subject-trie.js';

// ─── Structural JetStream lib types ─────────────────────────────────────────
//
// `@nats-io/jetstream` is a dynamic-imported peer dep — declaring its
// types at the import site would force the .d.ts to load even when the
// dynamic import isn't reached. These structural shapes cover exactly
// the API surface the FilterManager uses.

export interface ConsumerConfigLike {
  readonly name?: string;
  readonly durable_name?: string;
  readonly filter_subject?: string;
  readonly filter_subjects?: readonly string[];
  readonly ack_policy: 'none' | 'explicit';
  readonly deliver_policy: 'new' | 'all' | 'by_start_sequence';
  readonly opt_start_seq?: number;
  readonly inactive_threshold?: number;
}

export interface ConsumerInfoLike {
  readonly name: string;
  readonly num_pending?: number;
}

/**
 * Mutable-only consumer fields accepted by `jsm.consumers.update()`.
 * `deliver_policy` and `opt_start_seq` are set at creation and CANNOT
 * change afterwards — passing them in an update body triggers a server
 * error ("deliver policy is X, but optional start sequence is also set"
 * or similar). Only the narrow filter list is mutable.
 */
export interface ConsumerUpdateConfigLike {
  readonly filter_subjects?: readonly string[];
  readonly inactive_threshold?: number;
}

export interface JsmLike {
  readonly consumers: {
    add: (stream: string, config: ConsumerConfigLike) => Promise<ConsumerInfoLike>;
    info: (stream: string, durable: string) => Promise<ConsumerInfoLike>;
    update: (
      stream: string,
      durable: string,
      config: ConsumerUpdateConfigLike,
    ) => Promise<ConsumerInfoLike>;
    delete: (stream: string, name: string) => Promise<unknown>;
  };
}

export interface JsLike {
  readonly consumers: {
    get: (stream: string, name: string) => Promise<ConsumerLike>;
  };
}

export interface ConsumerLike {
  consume: () => Promise<MessageIteratorLike>;
}

export interface MessageIteratorLike extends AsyncIterable<MessageLike> {
  stop: () => void;
  /** `ConsumerMessages.status()` — consumer_deleted / consumer_not_found / heartbeats_missed … */
  status?: () => AsyncIterable<ConsumerStatusLike>;
}

export interface ConsumerStatusLike {
  readonly type: string;
  readonly count?: number;
}

export interface MessageLike {
  readonly subject: string;
  readonly data: Uint8Array;
  readonly info?: { readonly streamSequence?: number };
}

// ─── Tuning ─────────────────────────────────────────────────────────────────

/** Debounce window: collect requestInterest/releaseInterest calls for
 *  this long before issuing `consumer.update()`. */
const DEBOUNCE_MS = 100;
/** Maximum time between the first pending change and a forced flush.
 *  Prevents a steady stream of additions from delaying flush indefinitely. */
const MAX_WAIT_MS = 250;
/** Retry transient JetStream management failures without spinning or leaving
 *  snapshot handoff waiters parked forever. */
const RECONCILE_RETRY_DELAYS_MS = [250, 500, 1_000, 2_000, 5_000] as const;
/** Delay before reconcile attempt `attempt + 1` (0-based), clamped to the last rung. */
/**
 * Consecutive failed `consumers.update` calls on one shard before the consumer
 * is replaced under a fresh durable name. A consumer the broker can no longer
 * persist answers every update with an error that is neither "not found" nor
 * transient; replacing it is the only way back to a live wire.
 */
const CONSUMER_REPLACE_AFTER_FAILURES = 3;
/**
 * Replacements a pattern may spend on a consumer that merely keeps failing
 * (store loss, repeated update failures) inside `CONSUMER_REPLACE_WINDOW_MS`.
 * A slow-but-alive control plane fails updates too, and each replacement is a
 * delete plus an add against that same control plane; past the budget the
 * shard retries plain updates every `REPLACE_EXHAUSTED_RETRY_MS` instead. A
 * consumer the broker reports gone is always recreated.
 */
const CONSUMER_REPLACE_BUDGET = 5;
const CONSUMER_REPLACE_WINDOW_MS = 10 * 60_000;
const REPLACE_EXHAUSTED_RETRY_MS = 60_000;
/** Recovery attempts past the reconcile ladder keep doubling up to this. */
const RECOVERY_RETRY_CAP_MS = 60_000;
/** One background retry for a delete the broker did not confirm. */
const DELETE_RETRY_DELAY_MS = 5_000;

/**
 * Durable names this tab asked the broker to delete, by base name → count. A
 * deleted name is never reused: re-adding it right after the delete (HMR,
 * identity flip) races the broker's store removal and leaves a consumer with
 * no directory behind it (`meta.inf.tmp: no such file or directory` on every
 * later update). Pinned on `globalThis` so an HMR module swap keeps the counts.
 */
const durableGenerations: Map<string, number> = (() => {
  const host = globalThis as unknown as { __morfi_durable_generations?: Map<string, number> };
  host.__morfi_durable_generations ??= new Map();
  return host.__morfi_durable_generations;
})();

function noteDurableDeleted(consumerName: string): void {
  if (consumerName === '') return;
  const base = consumerName.replace(/_g\d+$/, '');
  durableGenerations.set(base, (durableGenerations.get(base) ?? 0) + 1);
}

function reconcileRetryDelayMs(attempt: number): number {
  return (
    RECONCILE_RETRY_DELAYS_MS[Math.min(attempt, RECONCILE_RETRY_DELAYS_MS.length - 1)] ?? 5_000
  );
}

/** Delay before recovery attempt `attempt + 1` (0-based): the ladder, then doubling to the cap. */
function recoveryRetryDelayMs(attempt: number): number {
  const past = attempt - (RECONCILE_RETRY_DELAYS_MS.length - 1);
  if (past <= 0) return reconcileRetryDelayMs(attempt);
  return Math.min(RECOVERY_RETRY_CAP_MS, reconcileRetryDelayMs(attempt) * 2 ** past);
}
/** Release-grace window: a subject removed from the trie stays on the
 *  wire for this long, so sub-2s back-button revisits don't churn the
 *  consumer's filter_subjects list. Mirrors the registry's `graceMs`
 *  default. */
const RELEASE_GRACE_MS = 2_000;
/** Server-side inactivity reap for this tab's durables. Two minutes: long
 *  enough that nats-core's own reconnect (stale socket detected in ~10–15 s)
 *  heals a blip under the SAME consumer, short enough that a closed tab's
 *  consumers stop loading the broker quickly (every page load creates one
 *  per root; the population is what slows `consumers.add`/`update`, the
 *  JetStream control-plane call that timed out in prod). A reap that does
 *  happen — a laptop asleep past the window — is healed in place: the
 *  iterator's status stream reports it and `recoverShard` recreates the
 *  consumer with the same filters, then asks the hooks to re-fence. */
export const INACTIVE_THRESHOLD_NS = 120_000_000_000; // 2 min in nanoseconds
/** Replay-skip threshold: above this many pending events, skip replay
 *  and bind with DeliverNew (logged warning). Same value as the prior
 *  `openJetstreamSubsForAllowSub` implementation. */
const REPLAY_GATE_THRESHOLD = 2_000;

export type BoundedReplayResult =
  | { readonly status: 'replayed'; readonly eventCount: number }
  | { readonly status: 'reconciliation_required'; readonly reason: BoundedReplayFailureReason };

export type BoundedReplayFailureReason =
  | 'consumer_recreated'
  | 'invalid_anchor'
  | 'replay_too_large'
  | 'subject_not_allowed';

// ─── Idempotent consumer creation ───────────────────────────────────────────

/**
 * JetStream `consumers.add` is NOT idempotent — if a consumer with the same
 * durable name already exists (e.g. left over from a StrictMode double-mount,
 * an HMR rebuild that swapped the provider, or a previous bootstrap-retry
 * attempt that partially succeeded) the server returns 400 "consumer already
 * exists" and the whole bootstrap chain throws. This wrapper catches that
 * specific error, adopts the existing consumer via `info()`, and applies the
 * desired filter list via `update()` so the caller always gets a usable
 * `ConsumerInfoLike` back regardless of whether it was freshly created or
 * adopted.
 */
function isConsumerAlreadyExistsError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const obj = err as {
    readonly api_error?: { readonly err_code?: number };
    readonly message?: unknown;
  };
  if (typeof obj.api_error?.err_code === 'number' && obj.api_error.err_code === 10148) return true;
  const msg = typeof obj.message === 'string' ? obj.message.toLowerCase() : '';
  return msg.includes('consumer already exists') || msg.includes('consumer name already in use');
}

/**
 * Detect a "consumer not found" failure from `consumers.update()`. The
 * JetStream API code is 10014 (`JSConsumerNotFoundErr`); the lib also throws a
 * named `ConsumerNotFoundError`. Either means the durable was reaped
 * server-side (inactive_threshold) or lost on a stream/consumer reset while
 * the client believed it still existed — the recoverable case
 * `recreateConsumer` heals in place.
 */
function isConsumerNotFoundError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const obj = err as {
    readonly api_error?: { readonly err_code?: number };
    readonly name?: unknown;
    readonly message?: unknown;
  };
  if (typeof obj.api_error?.err_code === 'number' && obj.api_error.err_code === 10014) return true;
  const name = typeof obj.name === 'string' ? obj.name.toLowerCase() : '';
  if (name === 'consumernotfounderror') return true;
  const msg = typeof obj.message === 'string' ? obj.message.toLowerCase() : '';
  return msg.includes('consumer not found');
}

async function addOrAdoptConsumer(
  jsm: JsmLike,
  stream: string,
  cfg: ConsumerConfigLike,
): Promise<ConsumerInfoLike> {
  try {
    return await jsm.consumers.add(stream, cfg);
  } catch (err) {
    if (!isConsumerAlreadyExistsError(err)) throw err;
    const durable = cfg.name ?? cfg.durable_name;
    if (durable === undefined || durable === '') throw err;
    if (cfg.filter_subjects !== undefined) {
      try {
        await jsm.consumers.update(stream, durable, {
          filter_subjects: cfg.filter_subjects,
          ...(cfg.inactive_threshold !== undefined
            ? { inactive_threshold: cfg.inactive_threshold }
            : {}),
        });
      } catch {
        // The existing consumer's deliver_policy may be incompatible with
        // an update body change — fall through to `info()` and let the caller
        // decide. Worst case we adopt with the previous filter list and the
        // next FilterManager flush narrows it.
      }
    }
    return await jsm.consumers.info(stream, durable);
  }
}

// ─── Sentinel synthesis ─────────────────────────────────────────────────────

/**
 * Synthesize a "never-published" subject inside the pattern's scope. Used
 * as `filter_subjects: [SENTINEL]` when the SDK has interest in zero
 * concrete subjects under a pattern — keeps the consumer alive so the
 * lastSeq anchor stays valid and a repopulating viewport is one
 * `consumer.update()` away. The indexer never publishes these subjects.
 *
 *   morfi.market.*.>          → morfi.market.__sentinel__.__never__
 *   morfi.leaderboard.>       → morfi.leaderboard.__never__
 *   morfi.user.{x}.notifications → morfi.user.{x}.notifications  (concrete)
 */
export function sentinelForPattern(pattern: string): string {
  return pattern.replace(/>/g, '__never__').replace(/\*/g, '__sentinel__');
}

// ─── Wire-eligibility predicate ──────────────────────────────────────────────

/**
 * Decide whether a subject may enter a consumer's `filter_subjects` list.
 *
 * JetStream's `filter_subjects` accepts subjects that contain a single-
 * token `*` wildcard — it only rejects OVERLAPPING entries and `>`-rest
 * subjects mixed with siblings. So a BOUNDED subject like
 * `morfi.market.ABC.user.*.trade.v0` (concrete market, wildcard `actor`)
 * is a perfectly valid, non-overlapping wire filter — and it is exactly
 * the CORRECT subject for a per-row trade subscription (the publisher
 * emits `…user.<actorPubkey>.trade.v0` with a real, varying actor).
 *
 * What must NOT enter the wire is a FULLY-UNBOUND subject — a domain-pin
 * wildcard such as `morfi.market.*.user.*.trade.v0` (the entity id
 * itself is `*`) or a `>`-rest allow_sub root. Those would each overlap
 * every concrete per-row subject and collapse the wire back to "every
 * event under the root".
 *
 * Every Morfi subject is `morfi.<entity>.<id>.…` — entity ids live at
 * specific segment positions. A subject is wire-eligible iff:
 *   - it contains no `>` rest token, AND
 *   - at least one ENTITY position carries a concrete token (not `*`).
 *
 * Entity positions in Morfi grammar (after `morfi.<domain>.`):
 *   - index 2 (the primary entity id — always present)
 *   - index 4 (the secondary entity id — only when index 3 is the
 *     `user` literal, as in `morfi.market.<m>.user.<actor>.<X>.v0` or
 *     `morfi.amm.<mint>.user.<actor>.<X>.v0`).
 *
 * The viewer-narrowed activity subscriptions
 * (`morfi.market.*.user.<id>.{trade,backing,boost,claim}.v0`) are
 * wire-eligible via index 4 even when index 2 is `*`.
 *
 *   morfi.market.ABC.user.*.trade.v0     → eligible (idx 2 concrete)
 *   morfi.market.*.user.XYZ.trade.v0     → eligible (idx 4 concrete)
 *   morfi.market.created.v0              → eligible (idx 2 literal)
 *   morfi.user.XYZ.gift.v0               → eligible (idx 2 concrete)
 *   morfi.market.*.user.*.trade.v0       → NOT eligible (all entity wildcards)
 *   morfi.market.*.>                     → NOT eligible (rest token)
 */
export function isWireEligibleSubject(subject: string): boolean {
  if (subject.includes('>') || /[\s{}]/.test(subject)) return false;
  const segs = subject.split('.');
  if (segs.some((segment) => segment.length === 0)) return false;
  if (segs[2] !== undefined && segs[2] !== '*') return true;
  if (segs[3] === 'user' && segs[4] !== undefined && segs[4] !== '*') return true;
  return false;
}

// ─── PatternState ───────────────────────────────────────────────────────────

interface ConsumerShardState {
  readonly index: number;
  /** Consumer name (durable, per provider mount) — stable across update() calls. */
  consumerName: string;
  /** Last filter_subjects list pushed to the server. Used as the diff baseline. */
  currentFilters: readonly string[];
  /** Message iterator + cleanup. */
  iterator: MessageIteratorLike | null;
  /** Count of events actually delivered to this shard's consume loop. */
  eventsDelivered: number;
  /** A `recoverShard` loop is running for this shard. */
  recovering: boolean;
}

interface PatternState {
  readonly root: string; // e.g. 'morfi.market.*.>'
  readonly sentinel: string;
  /** Concrete subjects the SDK currently has interest in (matching this root). */
  readonly desired: Set<string>;
  /** Pending diff since last flush — used by the devtool for the "+/−" badge. */
  readonly pending: { adds: number; removes: number };
  /** Non-overlapping consumer partitions under this allow-sub root. */
  readonly shards: ConsumerShardState[];
  /** Cached lastSeq for devtool display (truth still lives in last-seq-store). */
  lastSeq: number | null;
  /** In-flight reconciliation promise so concurrent flushes serialize per pattern. */
  inFlightReconcile: Promise<void> | null;
  reconcileRetryTimer: ReturnType<typeof setTimeout> | null;
  reconcileRetryAttempt: number;
  /** Wall-clock ms of the first failed reconcile in the current streak. */
  reconcileFailingSince: number | null;
  /** Wall-clock ms of each budgeted consumer replacement, oldest first. */
  readonly replacedAt: number[];
}

// ─── FilterManager ──────────────────────────────────────────────────────────

export interface FilterManagerSnapshot {
  readonly patterns: ReadonlyArray<{
    readonly root: string;
    readonly consumerName: string;
    readonly filterSubjects: readonly string[];
    readonly lastSeq: number | null;
    readonly pendingDiff: { readonly adds: number; readonly removes: number };
    /** Lifetime count of events delivered to this consumer's iterator.
     *  Use as a delta over a time window to compute the SDK's actual
     *  receive rate vs. the publisher's total rate — the gap proves
     *  events for off-screen rows are NOT being delivered over the wire. */
    readonly eventsDelivered: number;
  }>;
}

export interface FilterManagerOpts {
  readonly jsm: JsmLike;
  readonly js: JsLike;
  readonly streamName: string;
  /** JWT allow_sub wildcards — one consumer shard is bootstrapped per root. */
  readonly roots: readonly string[];
  /** Stable per-tab id baked into the consumer durable name. */
  readonly tabNonce: number;
  readonly onMessage: MessageHandler;
  /**
   * A consumer was lost on the broker and recreated with the same filters.
   * Events published in the gap are gone from this consumer, so the
   * provider bumps the registry's wire version and every mounted hook
   * re-fences with one fresh snapshot.
   */
  readonly onConsumerRecovered?: () => void;
  /**
   * The connection is gone for good (the provider's `closed()` watcher). A
   * recovery loop stops instead of hammering delete+add through a dead
   * client; the replacement client brings its own manager.
   */
  readonly isClientClosed?: () => boolean;
  /**
   * Replay from the persisted per-root stream anchor at bootstrap. Off by
   * default: the anchor path is unverified against the REST snapshot fence
   * (DEFERRED.md DEF-15); seeded initial filters must not switch it on.
   */
  readonly replayFromAnchor?: boolean;
}

export type WireInterestReadinessReason = 'consumer_recreated';

export class WireInterestReadinessError extends Error {
  readonly reason: WireInterestReadinessReason;

  constructor(reason: WireInterestReadinessReason) {
    super(`Concrete JetStream interest is unavailable: ${reason}`);
    this.reason = reason;
  }
}

interface InterestWaiter {
  readonly resolve: () => void;
  readonly reject: (error: WireInterestReadinessError) => void;
}

export class FilterManager {
  private readonly opts: FilterManagerOpts;
  private readonly patterns = new Map<string, PatternState>();
  /** Awaiters that require the server to confirm one concrete filter. */
  private readonly interestWaiters = new Map<string, Set<InterestWaiter>>();
  /** Release-grace timers keyed by concrete subject. */
  private readonly releaseTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private firstPendingAt: number | null = null;
  private replayNonce = 0;
  private destroyed = false;
  /** Set once `init()` bound every root; flushes before that are deferred to it. */
  private initialized = false;

  constructor(opts: FilterManagerOpts) {
    this.opts = opts;
    for (const root of opts.roots) {
      const sentinel = sentinelForPattern(root);
      this.patterns.set(root, {
        root,
        sentinel,
        desired: new Set(),
        pending: { adds: 0, removes: 0 },
        shards: [],
        lastSeq: null,
        inFlightReconcile: null,
        reconcileRetryTimer: null,
        reconcileRetryAttempt: 0,
        reconcileFailingSince: null,
        replacedAt: [],
      });
    }
  }

  /**
   * Initialize all pattern consumers in parallel. A root with seeded
   * interest (`seedInterest`) is created bound to those filters straight
   * away; a root without any starts on its sentinel. The
   * `wireInterestRequest`/`wireInterestRelease` plumbing kicks in once
   * the registry begins firing transitions.
   */
  async init(): Promise<void> {
    await Promise.all(Array.from(this.patterns.values()).map((p) => this.initOne(p)));
    this.initialized = true;
    // Converge anything the seed could not cover in one consumer (overlapping
    // partitions) and any interest that arrived mid-bootstrap. `updateShard`
    // short-circuits on equal filters, so a clean seed costs nothing here.
    if (Array.from(this.patterns.values()).some((p) => p.desired.size > 0)) {
      this.scheduleFlush();
    }
  }

  /**
   * Pre-`init()` only: record the subjects the page already has interest in
   * so `initOne` creates each root's first consumer bound to them, skipping
   * the placeholder → `consumers.update` round trip. Ineligible or uncovered
   * subjects are dropped exactly as `add()` would drop them. Never flushes —
   * a flush before `init()` would create shard 0 twice under one durable.
   */
  seedInterest(subjects: readonly string[]): void {
    if (this.destroyed || this.initialized) return;
    for (const subject of subjects) {
      if (!isWireEligibleSubject(subject)) continue;
      const pattern = this.findPatternFor(subject);
      if (pattern === null) continue;
      pattern.desired.add(subject);
    }
  }

  /** Durable consumer names this tab currently owns (unload release). */
  getConsumerNames(): string[] {
    const names: string[] = [];
    for (const p of this.patterns.values()) {
      for (const shard of p.shards) {
        if (shard.consumerName !== '') names.push(shard.consumerName);
      }
    }
    return names;
  }

  /**
   * Concrete subject crossed 0→1 in the registry trie. Add it to the
   * matching pattern's desired set and schedule a debounced flush. Cancels
   * any pending release-grace timer (sub-2s revisit case).
   *
   * Fully-unbound subjects are silently ignored: list hooks
   * (`useGetMarkets`) register domain-pin wildcard observers in the trie
   * for client-side dispatch routing (e.g. `morfi.market.*.user.*.trade.v0`
   * → list-cache applyEvent). Those have no place in the server-side
   * `filter_subjects` array — they would overlap every concrete per-row
   * subject. BUT a bounded per-row subject that still carries a deeper
   * literal `*` (e.g. `morfi.market.ABC.user.*.trade.v0` — concrete
   * market, wildcard `actor`) IS a valid, non-overlapping wire filter
   * and MUST be admitted. See `isWireEligibleSubject`.
   */
  add(subject: string): void {
    if (this.destroyed) return;
    if (!isWireEligibleSubject(subject)) return;
    const releaseTimer = this.releaseTimers.get(subject);
    if (releaseTimer !== undefined) {
      clearTimeout(releaseTimer);
      this.releaseTimers.delete(subject);
    }
    const pattern = this.findPatternFor(subject);
    if (pattern === null) {
      // Not covered by any allow_sub root — server-side ACL would have
      // rejected a SUB anyway. Log and drop.
      console.warn(
        `[morfi-sdk] FilterManager: subject "${subject}" matches no allow_sub root; dropping.`,
      );
      return;
    }
    if (pattern.desired.has(subject)) return;
    pattern.desired.add(subject);
    pattern.pending.adds += 1;
    this.scheduleFlush();
  }

  /**
   * Resolve only after JetStream has accepted every requested concrete filter
   * and the matching consumer iterator is bound. This is the realtime
   * snapshot handoff boundary: the snapshot that releases the pre-snapshot
   * buffer — the SDK's authoritative post-wire refetch, or the sole fetch of a
   * `eagerSnapshot: false` hook — must not start before this settles; a
   * default hook's eager mount fetch may run earlier and is re-fetched once
   * this resolves.
   */
  awaitConcreteInterest(subjects: readonly string[]): Promise<void> {
    // A hook may request another user's private subject while its public REST
    // projection is still valid (for example, profile follower counts). That
    // subject can never bind under the viewer's credential, so waiting for it
    // would keep the REST query disabled forever. Preserve the handoff only
    // for subjects this connection is actually authorized to put on the wire.
    const concrete = subjects.filter(
      (subject) => isWireEligibleSubject(subject) && this.findPatternFor(subject) !== null,
    );
    return Promise.all(concrete.map((subject) => this.awaitSubjectInterest(subject))).then(
      (): void => undefined,
    );
  }

  /** Synchronous counterpart of `awaitConcreteInterest`. */
  hasConcreteInterest(subjects: readonly string[]): boolean {
    if (this.destroyed) return false;
    return subjects.every(
      (subject) =>
        !isWireEligibleSubject(subject) ||
        this.findPatternFor(subject) === null ||
        this.isSubjectActive(subject),
    );
  }

  /** Unlike the initial snapshot gate, polling needs authorized, active delivery. */
  hasPollingCoverage(subjects: readonly string[]): boolean {
    const concrete = subjects.filter(isWireEligibleSubject);
    return (
      !this.destroyed &&
      concrete.length > 0 &&
      concrete.every(
        (subject) => this.findPatternFor(subject) !== null && this.isSubjectActive(subject),
      )
    );
  }

  /**
   * Replay only the requested concrete filters from an authoritative stream
   * anchor. Dynamic consumer updates are DeliverNew and therefore cannot close
   * the post-snapshot filter-installation window themselves.
   */
  async replayBoundedFromSequence(
    subjects: readonly string[],
    startSequence: number,
  ): Promise<BoundedReplayResult> {
    if (this.destroyed) {
      return { status: 'reconciliation_required', reason: 'consumer_recreated' };
    }
    if (!Number.isSafeInteger(startSequence) || startSequence <= 0) {
      return { status: 'reconciliation_required', reason: 'invalid_anchor' };
    }

    const concrete = [...new Set(subjects)].sort();
    if (concrete.some((subject) => !isWireEligibleSubject(subject))) {
      return { status: 'reconciliation_required', reason: 'subject_not_allowed' };
    }
    const byPattern = new Map<PatternState, string[]>();
    for (const subject of concrete) {
      const pattern = this.findPatternFor(subject);
      if (pattern === null) {
        return { status: 'reconciliation_required', reason: 'subject_not_allowed' };
      }
      const filters = byPattern.get(pattern) ?? [];
      filters.push(subject);
      byPattern.set(pattern, filters);
    }

    let eventCount = 0;
    for (const [pattern, filters] of byPattern) {
      for (const partition of partitionNonOverlappingFilters(filters)) {
        const replay = await this.replayPartition(pattern, partition, startSequence);
        if (replay.status !== 'replayed') return replay;
        eventCount += replay.eventCount;
      }
    }
    return { status: 'replayed', eventCount };
  }

  /**
   * Concrete subject crossed 1→0 in the registry trie. Start the
   * release-grace timer; if no re-add lands within the window, drop the
   * subject from desired and flush.
   */
  remove(subject: string): void {
    if (this.destroyed) return;
    if (!isWireEligibleSubject(subject)) return;
    if (this.releaseTimers.has(subject)) return;
    const timer = setTimeout(() => {
      this.releaseTimers.delete(subject);
      const pattern = this.findPatternFor(subject);
      if (pattern === null || !pattern.desired.has(subject)) return;
      pattern.desired.delete(subject);
      pattern.pending.removes += 1;
      this.scheduleFlush();
    }, RELEASE_GRACE_MS);
    this.releaseTimers.set(subject, timer);
  }

  /** Debug snapshot — read by the devtool's `useSdkWirePatterns` hook. */
  getSnapshot(): FilterManagerSnapshot {
    return {
      patterns: Array.from(this.patterns.values()).flatMap((p) =>
        p.shards.map((shard) => ({
          root: p.root,
          consumerName: shard.consumerName,
          filterSubjects: shard.currentFilters,
          lastSeq: p.lastSeq,
          pendingDiff: { adds: p.pending.adds, removes: p.pending.removes },
          eventsDelivered: shard.eventsDelivered,
        })),
      ),
    };
  }

  /**
   * Neutralise this manager ahead of an identity-flip connection drain,
   * WITHOUT deleting its durable consumers (the post-flip manager re-adopts
   * them by name). Sets `destroyed` so any future or in-flight flush bails,
   * and cancels the pending debounce timer — so a scheduled `consumers.update`
   * can't fire against the closing connection (a benign-but-noisy
   * `ClosedConnectionError`). Idempotent. The full teardown (consumer delete +
   * iterator stop) still runs later via the subscription-effect cleanup.
   */
  freeze(): void {
    this.destroyed = true;
    this.rejectInterestWaiters('consumer_recreated');
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.clearReconcileRetries();
  }

  async teardown(): Promise<void> {
    this.destroyed = true;
    this.rejectInterestWaiters('consumer_recreated');
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.clearReconcileRetries();
    for (const t of this.releaseTimers.values()) clearTimeout(t);
    this.releaseTimers.clear();
    for (const p of this.patterns.values()) {
      for (const shard of p.shards) {
        try {
          shard.iterator?.stop();
        } catch {
          // Already stopped.
        }
        await this.deleteConsumer(shard.consumerName);
      }
    }
  }

  // ─── Internals ────────────────────────────────────────────────────────────

  private findPatternFor(subject: string): PatternState | null {
    for (const p of this.patterns.values()) {
      if (subjectMatches(p.root, subject)) return p;
    }
    return null;
  }

  private clientClosed(): boolean {
    return this.opts.isClientClosed?.() === true;
  }

  /** The iterator is still the shard's bound one and this manager can act on it. */
  private ownsLiveIterator(shard: ConsumerShardState, iterator: MessageIteratorLike): boolean {
    return !this.destroyed && !this.clientClosed() && shard.iterator === iterator;
  }

  /**
   * Best-effort delete. The broker not confirming (timeout, blip) gets one
   * background retry so a consumer with a live pull is not left for the
   * inactivity reap; the name is retired either way (see `noteDurableDeleted`).
   */
  private async deleteConsumer(name: string): Promise<void> {
    if (name === '') return;
    try {
      await this.opts.jsm.consumers.delete(this.opts.streamName, name);
    } catch (err) {
      if (!isConsumerNotFoundError(err)) {
        setTimeout(() => {
          this.opts.jsm.consumers.delete(this.opts.streamName, name).catch(() => undefined);
        }, DELETE_RETRY_DELAY_MS);
      }
    }
    noteDurableDeleted(name);
  }

  /** Spend one budgeted replacement; false once the window's budget is gone. */
  private takeReplaceBudget(p: PatternState): boolean {
    const cutoff = Date.now() - CONSUMER_REPLACE_WINDOW_MS;
    while (p.replacedAt.length > 0 && (p.replacedAt[0] ?? 0) < cutoff) p.replacedAt.shift();
    if (p.replacedAt.length >= CONSUMER_REPLACE_BUDGET) return false;
    p.replacedAt.push(Date.now());
    return true;
  }

  private replaceBudgetExhausted(p: PatternState): boolean {
    const cutoff = Date.now() - CONSUMER_REPLACE_WINDOW_MS;
    return p.replacedAt.filter((at) => at >= cutoff).length >= CONSUMER_REPLACE_BUDGET;
  }

  private async replayPartition(
    pattern: PatternState,
    filters: readonly string[],
    startSequence: number,
  ): Promise<BoundedReplayResult> {
    this.replayNonce += 1;
    const replayName = `${this.durableNameFor(pattern.root)}_catchup_${this.replayNonce}`;
    let iterator: MessageIteratorLike | null = null;
    try {
      // Named but not durable: the replay consumer lives for one bounded read,
      // so the broker keeps it in memory instead of persisting its state to the
      // stream store on every create/delete.
      const info = await this.opts.jsm.consumers.add(this.opts.streamName, {
        name: replayName,
        filter_subjects: filters,
        ack_policy: 'none',
        deliver_policy: 'by_start_sequence',
        opt_start_seq: startSequence,
        inactive_threshold: INACTIVE_THRESHOLD_NS,
      });
      const pending = info.num_pending ?? 0;
      if (pending > REPLAY_GATE_THRESHOLD) {
        return { status: 'reconciliation_required', reason: 'replay_too_large' };
      }
      if (pending === 0) return { status: 'replayed', eventCount: 0 };

      const consumer = await this.opts.js.consumers.get(this.opts.streamName, info.name);
      iterator = await consumer.consume();
      let delivered = 0;
      for await (const message of iterator) {
        if (this.destroyed) {
          return { status: 'reconciliation_required', reason: 'consumer_recreated' };
        }
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('wire.receivedEvents');
          _bumpPerf('wire.receivedBytes', message.data.byteLength);
          _observeWireArrival(message.data.byteLength);
        }
        const sequence = message.info?.streamSequence ?? 0;
        if (sequence <= 0) continue;
        recordLastSeq(pattern.root, sequence);
        pattern.lastSeq = Math.max(pattern.lastSeq ?? 0, sequence);
        if (process.env.NODE_ENV !== 'production') {
          const startedAt = performance.now();
          try {
            this.opts.onMessage(message.subject, message.data, sequence);
          } finally {
            _observe('wireDispatchDurationMs', performance.now() - startedAt);
          }
        } else {
          this.opts.onMessage(message.subject, message.data, sequence);
        }
        delivered += 1;
        if (delivered >= pending) return { status: 'replayed', eventCount: delivered };
      }
      return { status: 'reconciliation_required', reason: 'consumer_recreated' };
    } catch {
      return { status: 'reconciliation_required', reason: 'consumer_recreated' };
    } finally {
      try {
        iterator?.stop();
      } catch {
        // The temporary iterator may already be stopped by the transport.
      }
      try {
        await this.opts.jsm.consumers.delete(this.opts.streamName, replayName);
      } catch {
        // inactive_threshold reaps an already-lost temporary consumer.
      }
    }
  }

  private scheduleFlush(): void {
    if (this.destroyed || !this.initialized) return;
    const now = Date.now();
    if (this.firstPendingAt === null) this.firstPendingAt = now;
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    const elapsed = now - this.firstPendingAt;
    const delay = Math.max(0, Math.min(DEBOUNCE_MS, MAX_WAIT_MS - elapsed));
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.firstPendingAt = null;
      void this.flush();
    }, delay);
  }

  private async flush(): Promise<void> {
    if (this.destroyed) return;
    await Promise.all(Array.from(this.patterns.values()).map((p) => this.flushOne(p)));
  }

  private async flushOne(p: PatternState): Promise<void> {
    if (p.inFlightReconcile !== null) await p.inFlightReconcile;
    if (this.destroyed) return;
    const reconcile = this.reconcileShards(p);
    p.inFlightReconcile = reconcile;
    try {
      await reconcile;
    } finally {
      if (p.inFlightReconcile === reconcile) p.inFlightReconcile = null;
    }
  }

  private async reconcileShards(p: PatternState): Promise<void> {
    // JetStream rejects overlapping entries in one `filter_subjects` array.
    // Orthogonally-bounded patterns such as `amm.<mint>.user.*` and
    // `amm.*.user.<wallet>` therefore need separate consumers. First-fit
    // partitioning keeps every shard non-overlapping and adds no shard when
    // the ordinary per-row filters are already disjoint.
    const desired = Array.from(p.desired).sort();
    const partitions =
      desired.length === 0 ? [[p.sentinel]] : partitionNonOverlappingFilters(desired);
    const results = await Promise.all(
      partitions.map(async (filters, index) => {
        const existing = p.shards[index];
        if (existing !== undefined) return this.updateShard(p, existing, filters);
        return this.createShard(p, index, filters);
      }),
    );
    if (results.some((succeeded) => !succeeded)) {
      this.scheduleReconcileRetry(p);
      return;
    }

    const recoveredAfterRetry = p.reconcileRetryAttempt > 0;
    const failingSince = p.reconcileFailingSince;
    const attempts = p.reconcileRetryAttempt;
    this.resetReconcileRetry(p);
    if (recoveredAfterRetry) {
      reportNatsRecovery({
        stage: 'filter-update',
        code: 'NATS_FILTER_RECONCILE_RECOVERED',
        attempts: attempts + 1,
        downtimeMs: failingSince === null ? 0 : Math.max(0, Date.now() - failingSince),
        subject: p.root,
      });
    }
    const obsolete = p.shards.splice(partitions.length);
    await Promise.all(obsolete.map((shard) => this.deleteShard(shard)));
    p.pending.adds = 0;
    p.pending.removes = 0;
    this.resolveInterestWaiters(p);
  }

  private scheduleReconcileRetry(p: PatternState): void {
    if (this.destroyed || p.reconcileRetryTimer !== null) return;
    const delay = this.replaceBudgetExhausted(p)
      ? REPLACE_EXHAUSTED_RETRY_MS
      : reconcileRetryDelayMs(p.reconcileRetryAttempt);
    p.reconcileRetryAttempt += 1;
    p.reconcileRetryTimer = setTimeout(() => {
      p.reconcileRetryTimer = null;
      void this.flushOne(p);
    }, delay);
  }

  private resetReconcileRetry(p: PatternState): void {
    if (p.reconcileRetryTimer !== null) clearTimeout(p.reconcileRetryTimer);
    p.reconcileRetryTimer = null;
    p.reconcileRetryAttempt = 0;
    p.reconcileFailingSince = null;
  }

  private clearReconcileRetries(): void {
    for (const pattern of this.patterns.values()) this.resetReconcileRetry(pattern);
  }

  private async updateShard(
    p: PatternState,
    shard: ConsumerShardState,
    filters: readonly string[],
  ): Promise<boolean> {
    if (arraysEqual(filters, shard.currentFilters)) return true;
    // A recovery owns the shard: it rebinds with the filters it captured and
    // the reconcile retry converges the rest. Two recreates on one shard
    // would leave both consumers delivering.
    if (shard.recovering) return false;
    try {
      // ONLY pass mutable fields. `deliver_policy` and `opt_start_seq`
      // are immutable after consumer creation.
      await this.opts.jsm.consumers.update(this.opts.streamName, shard.consumerName, {
        filter_subjects: filters,
        inactive_threshold: INACTIVE_THRESHOLD_NS,
      });
      shard.currentFilters = filters;
      return true;
    } catch (err) {
      if (this.destroyed) return false;
      const failures = p.reconcileRetryAttempt + 1;
      const reaped = isConsumerNotFoundError(err);
      const broken = isConsumerStoreError(err) || failures >= CONSUMER_REPLACE_AFTER_FAILURES;
      const replace = reaped || (broken && this.takeReplaceBudget(p));
      if (!replace) {
        this.reportShardFailure(p, shard, 'filter-update', 'update', err);
        return false;
      }
      // Reaped, store-less, or stuck after repeated failures: replace the
      // consumer under a fresh durable name instead of updating a dead one.
      // The gap lost events for these filters; hooks re-fence.
      if (!reaped) this.reportShardFailure(p, shard, 'filter-update', 'update', err);
      const lostAt = p.reconcileFailingSince ?? Date.now();
      shard.recovering = true;
      try {
        await this.recreateConsumer(p, shard, filters);
      } catch (recreateErr) {
        this.reportShardFailure(p, shard, 'filter-update', 'recreate', recreateErr);
        return false;
      } finally {
        shard.recovering = false;
      }
      if (!reaped) {
        reportNatsRecovery({
          stage: 'consumer-recovery',
          code: 'NATS_FILTER_RECONCILE_RECOVERED',
          attempts: failures,
          downtimeMs: Math.max(0, Date.now() - lostAt),
          subject: p.root,
        });
        this.resetReconcileRetry(p);
      }
      this.opts.onConsumerRecovered?.();
      return true;
    }
  }

  private async createShard(
    p: PatternState,
    index: number,
    filters: readonly string[],
  ): Promise<boolean> {
    try {
      const durableName = this.durableNameFor(p.root, index);
      const info = await addOrAdoptConsumer(this.opts.jsm, this.opts.streamName, {
        name: durableName,
        durable_name: durableName,
        filter_subjects: filters,
        ack_policy: 'none',
        deliver_policy: 'new',
        inactive_threshold: INACTIVE_THRESHOLD_NS,
      });
      const shard: ConsumerShardState = {
        index,
        consumerName: info.name,
        currentFilters: filters,
        iterator: null,
        eventsDelivered: 0,
        recovering: false,
      };
      await this.bindIterator(p, shard);
      p.shards[index] = shard;
      return true;
    } catch (err) {
      if (!this.destroyed) {
        this.reportShardFailure(p, { index, consumerName: '' }, 'filter-update', 'create', err);
      }
      return false;
    }
  }

  /**
   * One failed control-plane call on a shard, reported with the reconcile
   * ladder's next delay so the log says when it retries. `updateShard`'s
   * caller (`reconcileShards`) schedules that retry; this only reports.
   */
  private reportShardFailure(
    p: PatternState,
    shard: { readonly index: number; readonly consumerName: string },
    stage: 'filter-update' | 'consumer-recovery',
    call: string,
    err: unknown,
  ): void {
    if (p.reconcileFailingSince === null) p.reconcileFailingSince = Date.now();
    const attempt = p.reconcileRetryAttempt + 1;
    const nextRetryMs = reconcileRetryDelayMs(p.reconcileRetryAttempt);
    const { reason, isAuth } = classifyFailureReason(err);
    const consumer = shard.consumerName !== '' ? shard.consumerName : `shard ${shard.index}`;
    reportNatsFailure({
      phase: 'retry',
      stage,
      code: 'NATS_FILTER_UPDATE_FAILED',
      reason: `${call}:${reason}`,
      message: `${describeError(err)} (consumer ${consumer})`,
      isAuth,
      attempt,
      nextRetryMs,
      subject: p.root,
      error: err,
    });
  }

  /**
   * The broker dropped a consumer (inactivity reap, stream reset) or its
   * iterator died. Recreate it in place with the same filters, retrying on
   * the reconcile ladder for as long as this manager lives, then ask the
   * provider to re-fence the hooks (events in the gap are gone).
   */
  private async recoverShard(
    p: PatternState,
    shard: ConsumerShardState,
    cause: string,
  ): Promise<void> {
    if (this.destroyed || this.clientClosed() || shard.recovering) return;
    shard.recovering = true;
    const lostAt = Date.now();
    try {
      shard.iterator?.stop();
    } catch {
      // Already stopped.
    }
    // Not bound until recreated: readiness probes must not count it.
    shard.iterator = null;
    reportWireHealth(false, `consumer-lost:${cause}`);
    const filters = shard.currentFilters;
    for (let attempt = 1; !this.destroyed && !this.clientClosed(); attempt += 1) {
      try {
        await this.recreateConsumer(p, shard, filters);
        shard.recovering = false;
        reportWireHealth(true, 'consumer-recovered');
        reportNatsRecovery({
          stage: 'consumer-recovery',
          code: 'NATS_FILTER_RECONCILE_RECOVERED',
          attempts: attempt,
          downtimeMs: Math.max(0, Date.now() - lostAt),
          subject: p.root,
        });
        this.opts.onConsumerRecovered?.();
        return;
      } catch (err) {
        if (this.destroyed || this.clientClosed()) break;
        const delay = recoveryRetryDelayMs(attempt - 1);
        const { reason, isAuth } = classifyFailureReason(err);
        reportNatsFailure({
          phase: 'retry',
          stage: 'consumer-recovery',
          code: 'NATS_FILTER_UPDATE_FAILED',
          reason: `${cause}:${reason}`,
          message: describeError(err),
          isAuth,
          attempt,
          nextRetryMs: delay,
          subject: p.root,
          error: err,
        });
        await new Promise<void>((resolve) => setTimeout(resolve, delay));
      }
    }
    shard.recovering = false;
  }

  private async deleteShard(shard: ConsumerShardState): Promise<void> {
    // Detach before stopping: the loop end of a shard this manager retired
    // is not a lost consumer, and must not recreate one nobody tracks.
    const iterator = shard.iterator;
    shard.iterator = null;
    try {
      iterator?.stop();
    } catch {
      // Already stopped.
    }
    await this.deleteConsumer(shard.consumerName);
  }

  /** Durable consumer name — pattern root + tab nonce, stable for the
   *  consumer's lifetime so a post-flip manager can adopt it by name. A name
   *  this tab has deleted gets a `_g<n>` generation suffix and is never reused. */
  private durableNameFor(root: string, shardIndex = 0): string {
    const base = `morfi_sdk_${this.opts.tabNonce}_${root.replace(/[^a-zA-Z0-9]/g, '_')}`;
    const shardBase = shardIndex === 0 ? base : `${base}_shard_${shardIndex}`;
    const generation = durableGenerations.get(shardBase) ?? 0;
    return generation === 0 ? shardBase : `${shardBase}_g${generation}`;
  }

  private async initOne(p: PatternState): Promise<void> {
    const storedSeq = getLastSeq(p.root);
    const durableName = this.durableNameFor(p.root);

    // Initial filter_subjects: the seeded interest's first non-overlapping
    // partition when the page already knows what it wants (skips the
    // placeholder → update round trip), else the sentinel. Trie observers
    // fire `requestInterest` as later hooks mount and the debounced flush
    // narrows in.
    const desiredAtInit = Array.from(p.desired).sort();
    const seededFilters = partitionNonOverlappingFilters(desiredAtInit)[0] ?? [];
    const initialFilters: readonly string[] =
      seededFilters.length > 0 ? seededFilters : [p.sentinel];
    const baseConfig: ConsumerConfigLike = {
      name: durableName,
      durable_name: durableName,
      filter_subjects: initialFilters,
      ack_policy: 'none',
      deliver_policy: 'new',
      inactive_threshold: INACTIVE_THRESHOLD_NS,
    };
    // Replay is scoped to the subjects the SDK actually wants — NEVER the
    // full `[p.root]` wildcard (which would deliver every market's backlog
    // only to drop it client-side). Opt-in (`replayFromAnchor`): with seeded
    // filters the anchor path would otherwise switch itself on, and it is
    // unverified against the REST snapshot fence (DEF-15). Default is
    // DeliverNew; the per-query reconnect refetch covers catch-up.
    const replayFilters = seededFilters;
    const replayConfig: ConsumerConfigLike | null =
      this.opts.replayFromAnchor === true && storedSeq !== null && replayFilters.length > 0
        ? {
            ...baseConfig,
            filter_subjects: replayFilters,
            deliver_policy: 'by_start_sequence',
            opt_start_seq: storedSeq + 1,
          }
        : null;

    let info: ConsumerInfoLike;
    let boundFilters: readonly string[] = initialFilters;
    if (replayConfig !== null) {
      info = await addOrAdoptConsumer(this.opts.jsm, this.opts.streamName, replayConfig);
      const pending = info.num_pending ?? 0;
      if (pending > REPLAY_GATE_THRESHOLD) {
        console.warn(
          `[morfi-sdk] FilterManager: "${p.root}" has ${pending} pending events ` +
            `(threshold ${REPLAY_GATE_THRESHOLD}); skipping replay.`,
        );
        clearLastSeq(p.root);
        await this.opts.jsm.consumers.delete(this.opts.streamName, info.name);
        info = await addOrAdoptConsumer(this.opts.jsm, this.opts.streamName, baseConfig);
        boundFilters = initialFilters;
      } else {
        boundFilters = replayFilters;
      }
    } else {
      info = await addOrAdoptConsumer(this.opts.jsm, this.opts.streamName, baseConfig);
    }

    const shard: ConsumerShardState = {
      index: 0,
      consumerName: info.name,
      currentFilters: boundFilters,
      iterator: null,
      eventsDelivered: 0,
      recovering: false,
    };
    p.shards.push(shard);
    p.lastSeq = storedSeq;

    await this.bindIterator(p, shard);
    this.resolveInterestWaiters(p);
  }

  /**
   * Heal a consumer reaped server-side (inactive_threshold) or lost on a
   * stream/consumer reset — the recoverable `ConsumerNotFoundError` from
   * `flushOne`. Stops the dead iterator, recreates the durable on
   * `deliver_policy:'new'` bound straight to `filters` (sentinel when empty)
   * — no replay, so recovery never reopens the broad `[root]` window — and
   * rebinds the iterator. Missed events during the reap window are covered by
   * the per-query reconnect/stale refetch.
   */
  private async recreateConsumer(
    p: PatternState,
    shard: ConsumerShardState,
    filters: readonly string[],
  ): Promise<void> {
    try {
      shard.iterator?.stop();
    } catch {
      // Already stopped.
    }
    shard.iterator = null;
    // Replace, never revive: drop the lost or broken consumer best-effort and
    // take a fresh generation of its name so the add cannot race the delete.
    const previousName = shard.consumerName;
    shard.consumerName = '';
    await this.deleteConsumer(previousName);
    const durableName = this.durableNameFor(p.root, shard.index);
    const boundFilters: readonly string[] = filters.length === 0 ? [p.sentinel] : filters;
    const info = await addOrAdoptConsumer(this.opts.jsm, this.opts.streamName, {
      name: durableName,
      durable_name: durableName,
      filter_subjects: boundFilters,
      ack_policy: 'none',
      deliver_policy: 'new',
      inactive_threshold: INACTIVE_THRESHOLD_NS,
    });
    shard.consumerName = info.name;
    shard.currentFilters = boundFilters;
    await this.bindIterator(p, shard);
    this.resolveInterestWaiters(p);
  }

  private awaitSubjectInterest(subject: string): Promise<void> {
    if (this.destroyed) {
      return Promise.reject(new WireInterestReadinessError('consumer_recreated'));
    }
    if (this.isSubjectActive(subject)) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const waiters = this.interestWaiters.get(subject) ?? new Set<InterestWaiter>();
      waiters.add({ resolve, reject });
      this.interestWaiters.set(subject, waiters);
    });
  }

  private isSubjectActive(subject: string): boolean {
    for (const pattern of this.patterns.values()) {
      if (!subjectMatches(pattern.root, subject)) continue;
      if (
        pattern.shards.some(
          (shard) => shard.iterator !== null && shard.currentFilters.includes(subject),
        )
      ) {
        return true;
      }
    }
    return false;
  }

  private resolveInterestWaiters(pattern: PatternState): void {
    for (const subject of Array.from(this.interestWaiters.keys())) {
      if (!subjectMatches(pattern.root, subject) || !this.isSubjectActive(subject)) continue;
      const waiters = this.interestWaiters.get(subject);
      this.interestWaiters.delete(subject);
      for (const waiter of waiters ?? []) waiter.resolve();
    }
  }

  private rejectInterestWaiters(reason: WireInterestReadinessReason): void {
    for (const waiters of this.interestWaiters.values()) {
      for (const waiter of waiters) waiter.reject(new WireInterestReadinessError(reason));
    }
    this.interestWaiters.clear();
  }

  /**
   * Bind (or rebind) the message-iterator loop for a shard. Shared by
   * `initOne` and `recreateConsumer`. Records per-root lastSeq + forwards each
   * event via `onMessage`, reconciling a stale replay anchor on the first
   * delivery (stream-reset detection).
   */
  private async bindIterator(p: PatternState, shard: ConsumerShardState): Promise<void> {
    const consumer = await this.opts.js.consumers.get(this.opts.streamName, shard.consumerName);
    const iterator = await consumer.consume();
    shard.iterator = iterator;
    this.watchIteratorStatus(p, shard, iterator);
    const root = p.root;
    const onMessage = this.opts.onMessage;
    // Replay anchor as it stood at consumer creation. Used once, on the
    // first delivered message, to detect a stream reset (see below).
    const storedAnchor = getLastSeq(root) ?? 0;
    // In-loop high-water — the ONLY cursor the per-message dedup may consult.
    // JetStream delivers in-order within a single consumer, so this strictly
    // advances; it starts at 0 so it never wrongly drops a live event. The
    // PERSISTED anchor is for `opt_start_seq` on the NEXT consumer creation
    // only — it must NOT gate live delivery.
    let loopHighWater = 0;
    let anchorReconciled = storedAnchor === 0;
    void (async () => {
      try {
        for await (const m of iterator) {
          // Increment delivered counter BEFORE seq dedup — this measures the
          // WIRE-level arrival rate, which verify scripts compare against a
          // side-channel raw subscription to prove the wire is narrow.
          shard.eventsDelivered += 1;
          if (process.env.NODE_ENV !== 'production') {
            _bumpPerf('wire.receivedEvents');
            _bumpPerf('wire.receivedBytes', m.data.byteLength);
            _observeWireArrival(m.data.byteLength);
          }
          const seq = m.info?.streamSequence ?? 0;
          if (seq <= 0) continue;
          // In-loop redelivery guard only. Do NOT compare against the persisted
          // anchor here: a stream reset rewinds JetStream's sequence numbers
          // below the anchor, and since `recordLastSeq` is monotonic the anchor
          // can never heal — gating on it would drop every event forever (the
          // local-stack-reset freeze bug).
          if (seq <= loopHighWater) continue;
          // First delivered message at-or-below the persisted replay anchor
          // means the stream was reset/recreated (its sequences rewound). The
          // monotonic `recordLastSeq` would refuse to rewind, leaving a
          // permanently-stale anchor — so drop it explicitly and let the store
          // re-seed from this fresh stream's sequence.
          if (!anchorReconciled) {
            anchorReconciled = true;
            if (seq <= storedAnchor) {
              clearLastSeq(root);
              console.warn(
                `[morfi-sdk] FilterManager: "${root}" delivered seq ${seq} ` +
                  `≤ stored anchor ${storedAnchor} — stream reset detected; ` +
                  'dropping stale replay anchor.',
              );
            }
          }
          loopHighWater = seq;
          recordLastSeq(root, seq);
          p.lastSeq = seq;
          if (process.env.NODE_ENV !== 'production') {
            const startedAt = performance.now();
            try {
              onMessage(m.subject, m.data, seq);
            } finally {
              _observe('wireDispatchDurationMs', performance.now() - startedAt);
            }
          } else {
            onMessage(m.subject, m.data, seq);
          }
        }
      } catch (err) {
        if (this.ownsLiveIterator(shard, iterator)) {
          void this.recoverShard(p, shard, `iterator_error:${classifyFailureReason(err).reason}`);
        }
        return;
      }
      // The loop ended without a teardown: the library gave up on the
      // consumer (deleted on the broker, missed heartbeats past its limit).
      if (this.ownsLiveIterator(shard, iterator)) {
        void this.recoverShard(p, shard, 'iterator_ended');
      }
    })();
  }

  /**
   * Consume the iterator's status stream so a consumer the broker reaped is
   * healed immediately instead of on the next filter update (which may be
   * minutes away on a static page). Absent on fakes that don't implement it.
   */
  private watchIteratorStatus(
    p: PatternState,
    shard: ConsumerShardState,
    iterator: MessageIteratorLike,
  ): void {
    const status = iterator.status;
    if (typeof status !== 'function') return;
    void (async (): Promise<void> => {
      try {
        for await (const st of status.call(iterator)) {
          if (!this.ownsLiveIterator(shard, iterator)) return;
          if (st.type === 'consumer_deleted' || st.type === 'consumer_not_found') {
            void this.recoverShard(p, shard, st.type);
            return;
          }
        }
      } catch {
        // The status stream ends with its iterator; the message loop owns recovery.
      }
    })();
  }
}

function partitionNonOverlappingFilters(filters: readonly string[]): readonly string[][] {
  const partitions: string[][] = [];
  for (const filter of filters) {
    const available = partitions.find((partition) =>
      partition.every((existing) => !filterSubjectsOverlap(existing, filter)),
    );
    if (available === undefined) {
      partitions.push([filter]);
    } else {
      available.push(filter);
    }
  }
  return partitions;
}

function filterSubjectsOverlap(left: string, right: string): boolean {
  const leftSegments = left.split('.');
  const rightSegments = right.split('.');
  if (leftSegments.length !== rightSegments.length) return false;
  return leftSegments.every(
    (segment, index) =>
      segment === '*' || rightSegments[index] === '*' || segment === rightSegments[index],
  );
}

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ─── Singleton handle (devtool surface) ─────────────────────────────────────

let pinned: FilterManager | null = null;

export function pinFilterManager(fm: FilterManager): void {
  pinned = fm;
  (globalThis as unknown as { __morfi_filter_manager?: FilterManager }).__morfi_filter_manager = fm;
}

export function getFilterManagerSnapshot(): FilterManagerSnapshot | null {
  if (pinned === null) return null;
  return pinned.getSnapshot();
}

export function clearPinnedFilterManager(): void {
  pinned = null;
  delete (globalThis as unknown as { __morfi_filter_manager?: FilterManager })
    .__morfi_filter_manager;
}

/**
 * Freeze the live FilterManager (if any) ahead of an identity-flip drain so
 * its debounced flush can't fire `consumers.update` against the closing
 * connection. No-op when nothing is pinned. The manager stays pinned; the
 * subscription effect swaps in the post-flip manager and runs the full
 * teardown.
 */
export function freezePinnedFilterManager(): void {
  pinned?.freeze();
}
